import React, { useEffect, useRef, useState } from 'react';
import EntityImage from '../EntityImage/EntityImage';
import {
  ENTITY_IMAGE_ACCEPT,
  formatFileSize,
  validateEntityImageDimensions,
  validateEntityImageFile,
} from '../../utils/entityImageFile';
import './EntityImagePicker.css';

/**
 * ТЗ V5 №12: выбор изображения с компьютера в форме объекта иерархии.
 * Файл не отправляется сразу — форма загружает его после сохранения полей.
 * Локальный предпросмотр живёт на blob-ссылке, которая освобождается при
 * смене файла и закрытии формы.
 */
const EntityImagePicker = ({
  entityType,
  entityId = null,
  currentImage = '',
  file = null,
  removeRequested = false,
  onFileChange,
  onRemoveChange,
  disabled = false,
}) => {
  const inputRef = useRef(null);
  const [error, setError] = useState('');
  const [previewUrl, setPreviewUrl] = useState('');
  const [dragOver, setDragOver] = useState(false);

  useEffect(() => {
    if (!file) {
      setPreviewUrl('');
      return undefined;
    }
    const url = URL.createObjectURL(file);
    setPreviewUrl(url);
    // Пиксельные ограничения видны только после декодирования картинки.
    let cancelled = false;
    if (typeof Image !== 'undefined') {
      const probe = new Image();
      probe.onload = () => {
        if (cancelled) return;
        const dimensionsError = validateEntityImageDimensions(probe.naturalWidth, probe.naturalHeight);
        if (dimensionsError) {
          setError(dimensionsError);
          onFileChange?.(null);
        }
      };
      probe.onerror = () => {
        if (cancelled) return;
        setError('Файл не удалось открыть как изображение');
        onFileChange?.(null);
      };
      probe.src = url;
    }
    return () => {
      cancelled = true;
      URL.revokeObjectURL(url);
    };
    // onFileChange — колбэк формы, его смена не меняет выбранный файл.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [file]);

  const pickFile = (picked) => {
    if (!picked) return;
    const validationError = validateEntityImageFile(picked);
    setError(validationError || '');
    if (validationError) return;
    onRemoveChange?.(false);
    onFileChange?.(picked);
  };

  const hasCurrent = Boolean(currentImage) && !removeRequested;

  const renderPreview = () => {
    if (file && previewUrl) return <img src={previewUrl} alt="Новое изображение" />;
    if (removeRequested) return <div className="eip-placeholder">Изображение будет удалено</div>;
    return (
      <EntityImage
        entityType={entityType}
        entityId={entityId}
        image={currentImage}
        alt="Текущее изображение"
        placeholder="Изображения нет"
        placeholderClassName="eip-placeholder"
      />
    );
  };

  return (
    <div className="mb-3">
      <div className="form-label">Изображение</div>
      <div
        className={`eip${dragOver ? ' eip--drag' : ''}`}
        onDragOver={(event) => { if (disabled) return; event.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={(event) => {
          event.preventDefault();
          setDragOver(false);
          if (!disabled) pickFile(event.dataTransfer?.files?.[0]);
        }}
      >
        <div className="eip-preview">{renderPreview()}</div>
        <div className="eip-body">
          {file && (
            <div className="eip-file">
              <span className="eip-file-name" title={file.name}>{file.name}</span>
              <span className="eip-file-size">{formatFileSize(file.size)}</span>
            </div>
          )}
          <div className="eip-actions">
            <button
              type="button"
              className="btn btn-outline-primary btn-sm"
              onClick={() => inputRef.current?.click()}
              disabled={disabled}
            >
              {file || hasCurrent ? 'Заменить' : 'Загрузить с компьютера'}
            </button>
            {file && (
              <button
                type="button"
                className="btn btn-outline-secondary btn-sm"
                onClick={() => { setError(''); onFileChange?.(null); }}
                disabled={disabled}
              >
                Отменить выбор
              </button>
            )}
            {!file && hasCurrent && (
              <button
                type="button"
                className="btn btn-outline-danger btn-sm"
                onClick={() => onRemoveChange?.(true)}
                disabled={disabled}
              >
                Удалить изображение
              </button>
            )}
            {!file && removeRequested && (
              <button
                type="button"
                className="btn btn-outline-secondary btn-sm"
                onClick={() => onRemoveChange?.(false)}
                disabled={disabled}
              >
                Не удалять
              </button>
            )}
          </div>
          {error && <p className="eip-error" role="alert">{error}</p>}
        </div>
        <input
          ref={inputRef}
          type="file"
          accept={ENTITY_IMAGE_ACCEPT}
          className="eip-input"
          aria-label="Файл изображения"
          onChange={(event) => {
            pickFile(event.target.files?.[0]);
            // Повторный выбор того же файла снова вызывает onChange.
            event.target.value = '';
          }}
          tabIndex={-1}
        />
      </div>
    </div>
  );
};

export default EntityImagePicker;
